import { useMemo } from 'react';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

import { APIV3ErrorData } from '@dbt-labs/app-nav';

import { Project } from './project';
import { API_V3, ApiV3Response, PRIVATE_API, PrivateApiResponse } from './types';

export interface FavoriteProject {
  id: number;
  account_id?: number;
  user_id?: number;
  project_id?: number;
}

export const getFavoriteProjects = async (
  accountId: number,
): Promise<FavoriteProject[]> => {
  const response = await axios.get<PrivateApiResponse<FavoriteProject[]>>(
    `${PRIVATE_API}/accounts/${accountId}/favorite-projects/`,
    {},
  );

  return response?.data?.data;
};

export const useFavoriteProjects = (
  accountId?: number,
  options?: Omit<
    UseQueryOptions<FavoriteProject[], AxiosError>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) => {
  return useQuery<FavoriteProject[], AxiosError>({
    queryKey: ['favorite-projects', accountId],
    queryFn: () => getFavoriteProjects(accountId ?? 0),
    enabled: !!accountId,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const getFavoriteProjectsData = async (
  accountId?: number,
  projectIds?: number[],
  includeRelated?: string[],
): Promise<Project[]> => {
  const response = await axios.get<ApiV3Response<Project[]>>(
    `${API_V3}/accounts/${accountId}/projects/`,
    {
      params: {
        limit: 100,
        order_by: 'name',
        ...(projectIds && projectIds.length > 0
          ? { pk__in: JSON.stringify(projectIds) }
          : {}),
        include_related: includeRelated ? JSON.stringify(includeRelated) : undefined,
      },
    },
  );

  return response?.data?.data;
};

export const useFavoriteProjectsData = (accountId?: number, projectIds?: number[]) => {
  return useQuery<Project[], AxiosError>({
    queryKey: ['favorite-projects-data', accountId],
    queryFn: () => getFavoriteProjectsData(accountId, projectIds),
    enabled: !!accountId && !!projectIds && projectIds.length > 0,
  });
};

export const useFavoriteProjectIds = (accountId: number) => {
  const query = useFavoriteProjects(accountId);
  const favoriteProjectIds = useMemo(
    () => query.data?.flatMap((fp) => fp.project_id ?? []) ?? [],
    [query.data],
  );

  return {
    favoriteProjectIds,
    isLoading: query.isLoading,
  };
};

export const addFavoriteProject = async (
  accountId: number,
  projectId: number,
): Promise<FavoriteProject> => {
  const response = await axios.post<PrivateApiResponse<FavoriteProject>>(
    `${PRIVATE_API}/accounts/${accountId}/favorite-projects/modify/`,
    { project_id: projectId },
  );

  return response?.data?.data;
};

type AddFavoriteProjectVariables = {
  accountId: number;
  projectId: number;
};

export const useAddFavoriteProject = (
  shouldInvalidate: boolean = true,
): UseMutationResult<
  FavoriteProject,
  AxiosError<ApiV3Response<APIV3ErrorData>>,
  AddFavoriteProjectVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, projectId }) => addFavoriteProject(accountId, projectId),
    onSettled: (_, __, vars) => {
      if (vars?.accountId && shouldInvalidate) {
        queryClient.invalidateQueries({
          queryKey: ['favorite-projects', vars.accountId],
        });
      }
    },
  });
};

export const deleteFavoriteProject = async (
  accountId: number,
  projectId: number,
): Promise<FavoriteProject> => {
  const response = await axios.delete<PrivateApiResponse<FavoriteProject>>(
    `${PRIVATE_API}/accounts/${accountId}/favorite-projects/modify/`,
    { data: { project_id: projectId } },
  );

  return response?.data?.data;
};

export const useDeleteFavoriteProject = (
  shouldInvalidate: boolean = true,
): UseMutationResult<
  FavoriteProject,
  AxiosError<ApiV3Response<APIV3ErrorData>>,
  AddFavoriteProjectVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, projectId }) =>
      deleteFavoriteProject(accountId, projectId),
    onSettled: (_, __, vars) => {
      if (vars?.accountId && shouldInvalidate) {
        queryClient.invalidateQueries({
          queryKey: ['favorite-projects', vars.accountId],
        });
      }
    },
  });
};
